// JS error + network failure capture.
//
// Hooks:
//   - window.onerror           → uncaught synchronous errors
//   - window.unhandledrejection → promise rejections
//   - monkey-patch fetch        → log non-2xx responses and exceptions
//   - monkey-patch XHR          → same, for libraries that still use XHR
//
// We rate-limit hard to avoid amplifying error storms (e.g. a script that
// throws inside a render loop). At most ERROR_BUDGET errors per session,
// counted across both kinds.

import type {
  ErrorEvent as TrackerErrorEvent,
  NetworkErrorEvent,
} from "@hotmaps/shared/events";

export type ErrorEmit = (ev: TrackerErrorEvent | NetworkErrorEvent) => void;

const ERROR_BUDGET = 50;
const MSG_MAX = 500;
const STACK_MAX = 2000;
const URL_MAX = 300;

// `isOwnUrl` lets the caller (tracker.ts) mark requests to HotMaps's OWN
// telemetry hosts (ingest/cdn/live) so we NEVER log them as a network failure.
// Our beacons often report status 0 (sendBeacon / no-cors / ad-block) — without
// this filter the product flags its own tracker as a "broken element" on the
// customer's page. False positive + trust-killer.
export function registerErrorCapture(
  sessionStart: number,
  emit: ErrorEmit,
  isOwnUrl?: (url: string) => boolean,
) {
  let budget = ERROR_BUDGET;
  const own = (u: string): boolean => { try { return !!isOwnUrl && isOwnUrl(u); } catch { return false; } };

  function reltsNow(): number {
    return Date.now() - sessionStart;
  }
  function spend(): boolean {
    if (budget <= 0) return false;
    budget--;
    return true;
  }

  // --- JS errors --------------------------------------------------------
  // We use addEventListener (not assignment) so we don't clobber whatever
  // the host site has wired up.
  window.addEventListener("error", (e: ErrorEvent) => {
    if (!spend()) return;
    emit({
      t: "err",
      ts: reltsNow(),
      kind: "error",
      message: truncate(String(e.message ?? "Error"), MSG_MAX),
      stack: e.error?.stack ? truncate(String(e.error.stack), STACK_MAX) : undefined,
      source: e.filename ? truncate(e.filename, URL_MAX) : undefined,
      line: typeof e.lineno === "number" ? e.lineno : undefined,
      col: typeof e.colno === "number" ? e.colno : undefined,
    });
  });

  window.addEventListener("unhandledrejection", (e: PromiseRejectionEvent) => {
    if (!spend()) return;
    const reason = (e.reason ?? "rejection") as unknown;
    let message: string;
    let stack: string | undefined;
    if (reason instanceof Error) {
      message = reason.message;
      stack = reason.stack;
    } else {
      try {
        message = typeof reason === "string" ? reason : JSON.stringify(reason);
      } catch {
        message = "unhandled rejection";
      }
    }
    emit({
      t: "err",
      ts: reltsNow(),
      kind: "unhandledrejection",
      message: truncate(message, MSG_MAX),
      stack: stack ? truncate(stack, STACK_MAX) : undefined,
    });
  });

  // --- fetch ------------------------------------------------------------
  // Wrap window.fetch. We don't store request/response bodies (privacy +
  // size); just method, sanitised URL, status, and duration.
  if (typeof window.fetch === "function") {
    const origFetch = window.fetch.bind(window);
    window.fetch = async function (input: RequestInfo | URL, init?: RequestInit) {
      const started = Date.now();
      const method = (init?.method || "GET").toUpperCase();
      const url = sanitiseUrl(typeof input === "string" ? input : (input as Request).url ?? input.toString());
      try {
        const resp = await origFetch(input as RequestInfo, init);
        if (resp.status >= 400 && !own(url) && spend()) {
          emit({
            t: "net",
            ts: Date.now() - sessionStart,
            method,
            url,
            status: resp.status,
            durationMs: Date.now() - started,
            kind: "fetch",
          });
        }
        return resp;
      } catch (err) {
        if (!own(url) && spend()) {
          emit({
            t: "net",
            ts: Date.now() - sessionStart,
            method,
            url,
            status: 0,
            durationMs: Date.now() - started,
            kind: "fetch",
          });
        }
        throw err;
      }
    };
  }

  // --- XMLHttpRequest --------------------------------------------------
  // Lots of older code (jQuery, GA, libs in iframes) still uses XHR.
  if (typeof XMLHttpRequest !== "undefined") {
    type TaggedXhr = XMLHttpRequest & {
      __hm_method?: string;
      __hm_url?: string;
      __hm_started?: number;
    };
    const origOpen = XMLHttpRequest.prototype.open;
    const origSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (this: TaggedXhr, method: string, url: string) {
      this.__hm_method = method.toUpperCase();
      this.__hm_url = sanitiseUrl(url);
      // eslint-disable-next-line prefer-rest-params
      return origOpen.apply(this, arguments as unknown as Parameters<typeof origOpen>);
    };

    XMLHttpRequest.prototype.send = function (this: TaggedXhr, body?: Document | XMLHttpRequestBodyInit | null) {
      this.__hm_started = Date.now();
      this.addEventListener("loadend", () => {
        // Status 0 = request aborted or never returned (CORS, offline, etc.)
        const bad = this.status === 0 || this.status >= 400;
        if (!bad || own(this.__hm_url ?? "") || !spend()) return;
        emit({
          t: "net",
          ts: Date.now() - sessionStart,
          method: this.__hm_method ?? "GET",
          url: this.__hm_url ?? "",
          status: this.status,
          durationMs: Date.now() - (this.__hm_started ?? Date.now()),
          kind: "xhr",
        });
      });
      // eslint-disable-next-line prefer-rest-params
      return origSend.apply(this, arguments as unknown as [XMLHttpRequestBodyInit?]);
    };
  }
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n) + "…" : s;
}

function sanitiseUrl(u: string): string {
  // Strip query VALUES but keep keys — keys often identify the endpoint
  // (?action=login), values often contain PII.
  try {
    const parsed = new URL(u, location.origin);
    const params: string[] = [];
    parsed.searchParams.forEach((_v, k) => params.push(k + "=*"));
    parsed.search = params.length ? "?" + params.join("&") : "";
    return truncate(parsed.toString(), URL_MAX);
  } catch {
    return truncate(u.replace(/\?.*$/, "?…"), URL_MAX);
  }
}
